import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../utils/supabase';
import { getConversationUrl } from '../../utils/conversationUrlHelpers';
import { useMessaging } from '../../contexts/MessagingContext';
import ErrorHandler from '../../utils/errorHandler';
import ConversationAvatar from './ConversationAvatar';
import ConversationListItem from './ConversationListItem';
import ChatMessageBubble from './ChatMessageBubble';
import EnhancedInterviewScheduleModal from '../interview/EnhancedInterviewScheduleModal';
import TemplateManagementModal from './TemplateManagementModal';
import QuickActionsModal from './QuickActionsModal';
import VAHeaderNav from '../navigation/VAHeaderNav';
import EmployerHeaderNav from '../navigation/EmployerHeaderNav';
import MessagesList from './MessagesList';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Icon from '../AppIcon';
import supabaseService from '../../services/supabaseService';
import unifiedMessagingService from '../../services/unifiedMessagingService';
import messageTemplateService from '../../services/messageTemplateService';
import TypingIndicator from './TypingIndicator';

/**
 * Unified messaging page component for both VA and employer users
 * Replaces separate messaging implementations with a single, role-adaptive interface
 * 
 * @param {string} preloadedConversationId - Conversation ID to preload (from secure URL)
 * @param {object} preloadedParticipant - Participant data (from secure URL resolution)
 * @param {boolean} disableSecureRedirect - If true, won't redirect to secure URLs (for robustness)
 */
const UnifiedMessagingPage = ({ 
  preloadedConversationId, 
  preloadedParticipant,
  disableSecureRedirect = false 
}) => {
  const { user, userProfile } = useAuth();
  const { 
    conversations, 
    selectedConversation, 
    messages, 
    isLoadingConversations, 
    isLoadingMessages, 
    loadConversations, 
    loadMoreConversations,
    conversationPagination,
    loadMessages, 
    sendMessage,
    sendMessageOptimistic,
    selectConversation,
    markAsRead,
    setMessages,
    updateConversationLastMessage
  } = useMessaging();
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const messagesEndRef = useRef(null);
  
  // Typing indicator state — driven by the shared chat: channel below
  const [isRecipientTyping, setIsRecipientTyping] = useState(false);
  const [typingUserName, setTypingUserName] = useState('');
  const typingSendTimeoutRef = useRef(null);
  const typingReceiveTimeoutRef = useRef(null);
  const isTypingSentRef = useRef(false);
  const TYPING_SEND_TIMEOUT_MS = 3000;
  const TYPING_RECEIVE_TIMEOUT_MS = 4000;

  // Stop and broadcast typing=false (defined first so broadcastTyping can reference it)
  const stopTyping = useCallback(() => {
    if (typingSendTimeoutRef.current) {
      clearTimeout(typingSendTimeoutRef.current);
      typingSendTimeoutRef.current = null;
    }
    if (!isTypingSentRef.current) return;
    isTypingSentRef.current = false;
    broadcastChannelRef.current?.send({
      type: 'broadcast',
      event: 'typing',
      payload: { userId: user?.id, isTyping: false }
    }).catch(() => {});
  }, [user?.id]);

  // Send a typing broadcast on the shared conversation channel
  const broadcastTyping = useCallback(() => {
    if (!broadcastChannelRef.current || !user?.id) return;
    if (isTypingSentRef.current) {
      // Already sent — just reset the auto-stop timer
      if (typingSendTimeoutRef.current) clearTimeout(typingSendTimeoutRef.current);
    } else {
      broadcastChannelRef.current.send({
        type: 'broadcast',
        event: 'typing',
        payload: { userId: user.id, userName: userProfile?.full_name || '', isTyping: true }
      }).catch(() => {});
      isTypingSentRef.current = true;
    }
    typingSendTimeoutRef.current = setTimeout(stopTyping, TYPING_SEND_TIMEOUT_MS);
  }, [user?.id, userProfile?.full_name, stopTyping]);

  // Clear typing state when conversation changes
  useEffect(() => {
    isTypingSentRef.current = false;
    if (typingSendTimeoutRef.current) { clearTimeout(typingSendTimeoutRef.current); typingSendTimeoutRef.current = null; }
    if (typingReceiveTimeoutRef.current) { clearTimeout(typingReceiveTimeoutRef.current); typingReceiveTimeoutRef.current = null; }
    setIsRecipientTyping(false);
    setTypingUserName('');
  }, [selectedConversation?.conversation_id]);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [messageSearchQuery, setMessageSearchQuery] = useState('');
  const [messageSearchResultIndex, setMessageSearchResultIndex] = useState(-1);
  const [showMessageSearch, setShowMessageSearch] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [filter, setFilter] = useState('all');
  const [isMobileView, setIsMobileView] = useState(false);
  const [showQuickActionModal, setShowQuickActionModal] = useState(false);
  const [quickActionType, setQuickActionType] = useState(null);
  
  // Custom templates state
  const [customTemplates, setCustomTemplates] = useState([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  
  // Enhanced scrolling and performance states
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [messageViewMode, setMessageViewMode] = useState('recent'); // 'recent' | 'all' | 'paginated'
  const [visibleMessageCount, setVisibleMessageCount] = useState(30); // Start with 30 messages
  const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);
  
  // Performance tracking
  const messageContainerRef = useRef(null);
  const lastScrollTop = useRef(0);
  const scrollTimeout = useRef(null);
  const isNearBottomRef = useRef(true);
  
  // Keep ref in sync with state
  useEffect(() => {
    isNearBottomRef.current = isNearBottom;
  }, [isNearBottom]);

  // Cleanup scroll timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
  }, []);
  
  // Refs for robust realtime handling
  const processedMessageIds = useRef(new Set());
  const lastConversationRefresh = useRef(0);
  const conversationRefreshThrottle = useRef(null);
  const isSubscribedRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const hasSubscribedOnceRef = useRef(false); // Track initial vs reconnect subscribe
  const syncIntervalRef = useRef(null);
  const postSendSyncTimerRef = useRef(null);
  const throttledConversationRefreshRef = useRef(null);
  const broadcastChannelRef = useRef(null); // Shared broadcast channel for real-time delivery

  // "Actively chatting" state: suppress seen/read receipts while user is sending messages
  const isActivelyChatting = useRef(false);
  const activeChatCooldownRef = useRef(null);
  const pendingReadConversationRef = useRef(null); // conversation ID that needs read-marking after cooldown
  const updateConversationLastMessageRef = useRef(updateConversationLastMessage);
  const selectedConversationRef = useRef(selectedConversation);
  const userRef = useRef(user);
  const userProfileRef = useRef(userProfile);

  // Keep refs in sync so realtime handlers access latest state without causing re-subscriptions
  const markConversationAsReadRef = useRef(null);
  useEffect(() => { selectedConversationRef.current = selectedConversation; }, [selectedConversation]);
  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { userProfileRef.current = userProfile; }, [userProfile]);
  useEffect(() => { updateConversationLastMessageRef.current = updateConversationLastMessage; }, [updateConversationLastMessage]);

  // Get user role for role-specific UI adaptations
  const userRole = userProfile?.role || 'va';
  const isEmployer = userRole === 'employer';

  // Force-refresh conversations on mount to catch messages received while on other pages
  useEffect(() => {
    if (user?.id) {
      loadConversations(true);
    }
  }, []); // Intentionally mount-only

  // Silently merge remote messages into local state without loading indicators.
  // Used by periodic sync, post-reconnect recovery, and post-send confirmation.
  const silentSyncMessages = useCallback(async (conversationId) => {
    if (!conversationId) return;
    try {
      // Always skip Redis cache for background sync — we need fresh DB data
      const result = await unifiedMessagingService.getMessages(conversationId, { skipCache: true });
      if (!result.success || !result.data) return;

      setMessages(prev => {
        if (prev.length === 0) return result.data;
        const localIds = new Set(prev.filter(m => !m._optimistic).map(m => m.id));
        const missed = result.data.filter(m => !localIds.has(m.id));
        // Preserve any optimistic messages that haven't yet been confirmed by DB.
        // These represent in-flight sends; removing them would make messages
        // appear to vanish and would require a page refresh to recover.
        const pendingOptimistic = prev.filter(m => m._optimistic);
        if (missed.length === 0 && pendingOptimistic.length === 0) return prev;
        if (missed.length === 0) return prev; // Keep prev with optimistic messages intact
        return [...prev.filter(m => !m._optimistic), ...missed]
          .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      });
    } catch {
      // Silent — never surface background sync errors
    }
  }, [setMessages]);

  // Silently sync missed messages when tab regains visibility
  // Handles WebSocket disconnects during background/sleep without showing loading spinner
  useEffect(() => {
    let isMounted = true;

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;

      const convId = selectedConversationRef.current?.conversation_id;
      // Refresh the conversation list sidebar
      loadConversations(true);

      // Silently sync messages for the active conversation only
      // The periodic sync interval handles ongoing checks; this just covers tab-resume
      if (convId) {
        // Small delay to let the WebSocket reconnect first
        await new Promise(resolve => setTimeout(resolve, 500));
        if (isMounted) {
          silentSyncMessages(convId);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      isMounted = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadConversations, silentSyncMessages]);

  // Periodic heartbeat sync: every 15 seconds, silently verify local state matches DB.
  // Catches any realtime events lost to network micro-interruptions or server throttling.
  useEffect(() => {
    if (!selectedConversation?.conversation_id) {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
        syncIntervalRef.current = null;
      }
      return;
    }

    const convId = selectedConversation.conversation_id;
    syncIntervalRef.current = setInterval(() => {
      // Only sync when the tab is visible to avoid wasted requests
      if (document.visibilityState === 'visible') {
        silentSyncMessages(convId);
      }
    }, 6000); // 6s heartbeat — fallback for missed realtime events

    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
        syncIntervalRef.current = null;
      }
    };
  }, [selectedConversation?.conversation_id, silentSyncMessages]);

  // Mark messages as read when conversation is opened - delegates to context
  // Suppressed while the user is actively chatting to avoid showing "Seen" to the other party mid-conversation
  const markConversationAsRead = useCallback(async (conversationId) => {
    if (!conversationId || !user?.id) return;

    if (isActivelyChatting.current) {
      // Defer: store the conversation ID so we can mark it after the cooldown
      pendingReadConversationRef.current = conversationId;
      return;
    }

    await markAsRead(conversationId);
    
    // Broadcast read receipt to the other participant via shared channel
    if (broadcastChannelRef.current) {
      const readMessageIds = messages
        .filter(m => m.recipientId === user.id || m.recipient_id === user.id)
        .filter(m => !m.isRead && !m._optimistic)
        .map(m => m.id);
      if (readMessageIds.length > 0) {
        broadcastChannelRef.current.send({
          type: 'broadcast',
          event: 'message_read',
          payload: { messageIds: readMessageIds, readAt: new Date().toISOString() }
        });
      }
    }
  }, [user?.id, markAsRead, messages]);
  // Keep ref in sync so preloaded effect can call it without adding it to deps
  useEffect(() => { markConversationAsReadRef.current = markConversationAsRead; }, [markConversationAsRead]);

  // Flush any deferred read-mark (called when cooldown expires or conversation changes)
  const flushPendingRead = useCallback(async () => {
    const pending = pendingReadConversationRef.current;
    pendingReadConversationRef.current = null;
    if (pending && user?.id) {
      await markAsRead(pending);
    }
  }, [user?.id, markAsRead]);

  // When the selected conversation changes, flush any pending read mark from the previous one
  // and reset the actively-chatting flag
  useEffect(() => {
    isActivelyChatting.current = false;
    if (activeChatCooldownRef.current) {
      clearTimeout(activeChatCooldownRef.current);
      activeChatCooldownRef.current = null;
    }
    flushPendingRead();
  }, [selectedConversation?.conversation_id, flushPendingRead]);

  // Load custom templates
  useEffect(() => {
    if (user?.id) {
      loadCustomTemplates();
    }
  }, [user?.id]);

  const loadCustomTemplates = async () => {
    setIsLoadingTemplates(true);
    try {
      const result = await messageTemplateService.getUserTemplates(user.id);
      if (result.success) {
        setCustomTemplates(result.data);
      } else {
        // Error logged via ErrorHandler
      }
    } catch (error) {
      // Error logged via ErrorHandler
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  // Message templates for both roles
  const messageTemplates = useMemo(() => {
    if (isEmployer) {
      return [
        {
          id: 1,
          name: 'Interview Invitation',
          content: 'Hi there! Thank you for your application. I\'d like to schedule an interview to discuss the position. Are you available this week?'
        },
        {
          id: 2,
          name: 'Project Kickoff',
          content: 'Hi! Welcome to the team. I\'m excited to start working with you. Let\'s schedule a kickoff call to discuss the project details.'
        },
        {
          id: 3,
          name: 'Follow-up',
          content: 'Hi! I wanted to follow up on our previous conversation. Do you have any updates or questions?'
        },
        {
          id: 4,
          name: 'Application Received',
          content: 'Hi! Thank you for applying to this position. I\'ve received your application and will review it shortly. I\'ll get back to you within 2-3 business days.'
        }
      ];
    } else {
      return [
        {
          id: 1,
          name: 'Introduction',
          content: 'Hi! I\'m interested in the position you posted. I\'d love to learn more about the role and how I can contribute to your team.'
        },
        {
          id: 2,
          name: 'Portfolio Share',
          content: 'Hi! Thank you for considering my application. I\'d be happy to share some examples of my previous work that align with your requirements.'
        },
        {
          id: 3,
          name: 'Availability Confirmation',
          content: 'Hi! I wanted to confirm my availability for this project. I\'m ready to start immediately and can commit the required hours.'
        },
        {
          id: 4,
          name: 'Question About Role',
          content: 'Hi! I have a few questions about the role requirements. Could we schedule a brief call to discuss the specifics?'
        }
      ];
    }
  }, [isEmployer]);

  // Combine default and custom templates
  const allTemplates = useMemo(() => {
    const defaultTemplates = messageTemplates.map(template => ({
      ...template,
      isDefault: true
    }));
    
    return [...customTemplates, ...defaultTemplates];
  }, [messageTemplates, customTemplates]);
  
  // Calculate unread conversations count
  const unreadCount = useMemo(() => {
    return conversations.filter(conv => conv.unread_count > 0).length;
  }, [conversations]);
  
  // Handle any preloaded conversation IDs from props
  useEffect(() => {
    // If we have a preloaded conversation ID from SecureConversationRoute, use it
    if (preloadedConversationId && user?.id) {
      // Guard: don't re-select if this conversation is already selected
      if (selectedConversation?.conversation_id === preloadedConversationId) {
        return;
      }

      // Find the conversation in the loaded conversations
      const conversationFromId = conversations.find(c => c.conversation_id === preloadedConversationId);
      if (conversationFromId) {
        selectConversation(conversationFromId);
        return;
      }
      
      // If not found in loaded conversations but we have a preloaded conversation ID, fetch it
      const fetchConversation = async () => {
        try {
          // Load messages for this conversation
          await loadMessages(preloadedConversationId);
          
          // If we have preloaded participant info, use it
          if (preloadedParticipant) {
            const conversationData = {
              conversation_id: preloadedConversationId,
              participant_name: preloadedParticipant.full_name,
              participant_avatar: preloadedParticipant.profile_picture,
              participant_1: user.id,  // Current user
              participant_2: preloadedParticipant.id  // Other participant
            };
            
            selectConversation(conversationData);
          }
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            // Error logged via ErrorHandler
          }
        }
      };
      
      // Execute the fetch
      fetchConversation();
    }
  }, [preloadedConversationId, conversations, user, selectConversation, loadMessages, preloadedParticipant, selectedConversation?.conversation_id]);

  // Handle preloaded conversation from secure URL resolution
  useEffect(() => {
    if (preloadedConversationId && conversations.length > 0) {
      const conversation = conversations.find(c => c.conversation_id === preloadedConversationId);
      if (conversation && selectedConversation?.conversation_id !== conversation.conversation_id) {
        selectConversation(conversation);
        // Mark messages as read when preloaded conversation is selected
        // Use ref to avoid re-firing this effect every time messages changes
        markConversationAsReadRef.current?.(preloadedConversationId);
      }
    }
  }, [preloadedConversationId, conversations, selectConversation, selectedConversation?.conversation_id]);  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (isNearBottom) {
      scrollToBottom();
    }
  }, [messages, isNearBottom]);

  // Load older messages function
  const loadOlderMessages = useCallback(() => {
    if (isLoadingOlderMessages || visibleMessageCount >= messages.length) return;
    
    setIsLoadingOlderMessages(true);
    
    // Increase visible message count by 50 (data already in memory)
    const newCount = Math.min(visibleMessageCount + 50, messages.length);
    setVisibleMessageCount(newCount);
    setIsLoadingOlderMessages(false);
  }, [isLoadingOlderMessages, visibleMessageCount, messages.length]);

  // Throttled conversation refresh to prevent rapid reloads
  const throttledConversationRefresh = useCallback((forConversationId = null, messagePayload = null) => {
    const now = Date.now();
    const THROTTLE_MS = 2000; // 2 second minimum between refreshes
    
    // If the message is for the currently selected conversation, update sidebar locally
    // instead of a full refresh — the message channel handles adding the actual message
    if (forConversationId && selectedConversation?.conversation_id === forConversationId) {
      if (messagePayload) {
        // Resolve sender name from local state (inline to avoid dependency ordering issues)
        let senderName = 'Unknown User';
        if (messagePayload.sender_id === userRef.current?.id) {
          senderName = userProfileRef.current?.full_name || 'You';
        } else {
          const conv = selectedConversationRef.current;
          senderName = conv?.participant_name || conv?.recipient_name || 'Unknown User';
        }
        updateConversationLastMessage(forConversationId, {
          content: messagePayload.content,
          timestamp: messagePayload.sent_at,
          senderId: messagePayload.sender_id,
          senderName
        });
      }
      return;
    }
    
    // Throttle refreshes
    if (now - lastConversationRefresh.current < THROTTLE_MS) {
      // Schedule a delayed refresh if not already scheduled
      if (!conversationRefreshThrottle.current) {
        conversationRefreshThrottle.current = setTimeout(() => {
          lastConversationRefresh.current = Date.now();
          // Force fresh load to bypass cache (important for restored conversations)
          loadConversations(true);
          conversationRefreshThrottle.current = null;
        }, THROTTLE_MS);
      }
      return;
    }
    
    lastConversationRefresh.current = now;
    // Force fresh load to bypass cache (important for restored conversations)
    loadConversations(true);
  }, [loadConversations, selectedConversation?.conversation_id, updateConversationLastMessage]);

  // Keep ref in sync so the conversation channel subscription stays stable
  useEffect(() => {
    throttledConversationRefreshRef.current = throttledConversationRefresh;
  }, [throttledConversationRefresh]);

  // Subscribe to real-time conversation updates to refresh when new messages arrive
  // Uses ref-based callback to prevent channel teardown when selectedConversation changes
  useEffect(() => {
    let conversationChannel = null;
    let isActive = true;
    
    if (user?.id) {
      // Set up a channel to listen for new conversations or conversation updates.
      // All conversation UPDATE/INSERT subscriptions use server-side participant filters
      // (participant_1 and participant_2) to prevent fan-out of unrelated events,
      // especially important for admin users who have broad RLS SELECT access.
      conversationChannel = supabase
        .channel(`conversations-${user.id}-unified`, {
          config: {
            broadcast: { self: false }
          }
        })
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'messages',
            filter: `recipient_id=eq.${user.id}`
          },
          (payload) => {
            if (!isActive) return;
            const newRow = payload.new;
            const messageConversationId = newRow?.conversation_id;

            // Update the sidebar (last message preview + unread count)
            throttledConversationRefreshRef.current?.(messageConversationId, newRow);

            // GUARANTEED delivery path: also push the message directly into the
            // active conversation's message list. This fires for every INSERT where
            // the current user is the recipient — it works even when the message
            // channel broadcast fails or the postgres_changes fallback is preempted.
            if (
              newRow?.id &&
              messageConversationId &&
              messageConversationId === selectedConversationRef.current?.conversation_id &&
              !processedMessageIds.current.has(newRow.id)
            ) {
              processedMessageIds.current.add(newRow.id);
              // Keep the Set bounded (same cap used in handleNewMessage)
              if (processedMessageIds.current.size > 200) {
                const iter = processedMessageIds.current.values();
                for (let i = 0; i < 100; i++) {
                  processedMessageIds.current.delete(iter.next().value);
                }
              }
              const sender = resolveSenderProfile(newRow.sender_id);
              const formattedMsg = {
                id: newRow.id,
                senderId: newRow.sender_id,
                recipientId: newRow.recipient_id,
                senderName: sender.name,
                senderAvatar: sender.avatar,
                content: newRow.content,
                timestamp: newRow.sent_at,
                type: newRow.message_type || 'text',
                isRead: newRow.is_read,
                readAt: newRow.read_at || null,
              };
              setMessages(prevMessages => {
                if (prevMessages.some(msg => msg.id === formattedMsg.id)) return prevMessages;
                const optimisticIdx = prevMessages.findIndex(
                  msg => msg._optimistic && msg.content === formattedMsg.content
                );
                if (optimisticIdx >= 0) {
                  const updated = [...prevMessages];
                  updated[optimisticIdx] = formattedMsg;
                  return updated;
                }
                return [...prevMessages, formattedMsg].sort(
                  (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
                );
              });
              if (isNearBottomRef.current) setTimeout(() => scrollToBottom(), 100);
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'conversations',
            filter: `participant_1=eq.${user.id}`
          },
          (payload) => {
            if (!isActive) return;
            throttledConversationRefreshRef.current?.(payload.new?.id);
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'conversations',
            filter: `participant_2=eq.${user.id}`
          },
          (payload) => {
            if (!isActive) return;
            throttledConversationRefreshRef.current?.(payload.new?.id);
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'conversations',
            filter: `participant_1=eq.${user.id}`
          },
          (payload) => {
            if (!isActive) return;
            throttledConversationRefreshRef.current?.();
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'conversations',
            filter: `participant_2=eq.${user.id}`
          },
          (payload) => {
            if (!isActive) return;
            throttledConversationRefreshRef.current?.();
          }
        )
        .subscribe();
    }
    
    return () => {
      isActive = false;
      if (conversationRefreshThrottle.current) {
        clearTimeout(conversationRefreshThrottle.current);
        conversationRefreshThrottle.current = null;
      }
      if (conversationChannel) {
        conversationChannel.unsubscribe();
        supabase.removeChannel(conversationChannel);
      }
    };
  }, [user?.id]); // Only depends on user ID — channel is never torn down during messaging

  // Clear processed message IDs when conversation changes
  useEffect(() => {
    processedMessageIds.current.clear();
    isSubscribedRef.current = false;
  }, [selectedConversation?.conversation_id]);

  // Helper: resolve sender profile from local state without a DB fetch
  const resolveSenderProfile = useCallback((senderId) => {
    // If sender is the current user, use their profile
    if (senderId === userRef.current?.id) {
      return {
        name: userProfileRef.current?.full_name || userRef.current?.email || 'You',
        avatar: userProfileRef.current?.profile_picture || null
      };
    }
    // Otherwise, use the selected conversation's participant data
    const conv = selectedConversationRef.current;
    if (conv) {
      return {
        name: conv.participant_name || conv.recipient_name || 'Unknown User',
        avatar: conv.participant_avatar || null
      };
    }
    return { name: 'Unknown User', avatar: null };
  }, []);

  // Subscribe to real-time message updates via shared broadcast channel.
  // Both participants in a conversation join the SAME channel (`chat:${conversationId}`).
  // New messages are delivered via broadcast (sender pushes after DB insert) which
  // bypasses postgres_changes RLS evaluation — fixing the VA→employer delivery issue.
  // postgres_changes is kept as a fallback for messages sent from other entry points
  // (e.g. FloatingChatBubble mini-chat). Deduplication prevents double-delivery.
  useEffect(() => {
    let messageChannel = null;
    let isActive = true;
    hasSubscribedOnceRef.current = false; // Reset for new conversation
    
    if (selectedConversation?.conversation_id && user?.id) {
      const conversationId = selectedConversation.conversation_id;
      
      // Shared channel name — both VA and employer subscribe to the same channel
      const channelName = `chat:${conversationId}`;
      
      // Handler to add a new message to local state (shared by broadcast + pg_changes)
      const handleNewMessage = (formattedMessage) => {
        if (!isActive || !formattedMessage?.id) return;
        
        const messageId = formattedMessage.id;
        
        // Deduplicate messages using ref (prevents double-delivery from broadcast + pg_changes)
        if (processedMessageIds.current.has(messageId)) {
          return;
        }
        processedMessageIds.current.add(messageId);
        
        // Prevent memory leak - trim oldest entries when set gets large
        if (processedMessageIds.current.size > 200) {
          const iter = processedMessageIds.current.values();
          for (let i = 0; i < 100; i++) {
            processedMessageIds.current.delete(iter.next().value);
          }
        }
        
        // Add to messages if it doesn't already exist, or replace optimistic version
        setMessages(prevMessages => {
          if (prevMessages.some(msg => msg.id === formattedMessage.id)) {
            return prevMessages;
          }
          
          const optimisticIndex = prevMessages.findIndex(msg =>
            msg._optimistic &&
            msg.senderId === formattedMessage.senderId &&
            msg.content === formattedMessage.content
          );
          
          if (optimisticIndex >= 0) {
            const updated = [...prevMessages];
            updated[optimisticIndex] = formattedMessage;
            return updated;
          }
          
          const updatedMessages = [...prevMessages, formattedMessage];
          return updatedMessages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        });
        
        if (isNearBottomRef.current) {
          setTimeout(() => scrollToBottom(), 100);
        }
        
        updateConversationLastMessageRef.current?.(conversationId, formattedMessage);

        // Post-receive verification: silently confirms DB state 3s after delivery.
        // Catches any subsequent messages that the realtime channel may have missed
        // (e.g. if the WebSocket briefly disconnected during a rapid exchange).
        if (postSendSyncTimerRef.current) clearTimeout(postSendSyncTimerRef.current);
        postSendSyncTimerRef.current = setTimeout(() => {
          silentSyncMessages(conversationId);
        }, 3000);
      };
      
      messageChannel = supabase
        .channel(channelName, {
          config: { broadcast: { self: false } } // Don't receive own broadcasts
        })
        // Primary: broadcast delivery (instant, no RLS dependency)
        .on('broadcast', { event: 'new_message' }, ({ payload }) => {
          handleNewMessage(payload);
        })
        .on('broadcast', { event: 'typing' }, ({ payload }) => {
          if (!isActive || !payload || payload.userId === userRef.current?.id) return;
          if (payload.isTyping) {
            setIsRecipientTyping(true);
            setTypingUserName(payload.userName || 'Someone');
            if (typingReceiveTimeoutRef.current) clearTimeout(typingReceiveTimeoutRef.current);
            typingReceiveTimeoutRef.current = setTimeout(() => {
              setIsRecipientTyping(false);
              setTypingUserName('');
            }, TYPING_RECEIVE_TIMEOUT_MS);
          } else {
            setIsRecipientTyping(false);
            setTypingUserName('');
            if (typingReceiveTimeoutRef.current) { clearTimeout(typingReceiveTimeoutRef.current); typingReceiveTimeoutRef.current = null; }
          }
        })
        .on('broadcast', { event: 'message_read' }, ({ payload }) => {
          if (!isActive || !payload?.messageIds) return;
          setMessages(prevMessages => 
            prevMessages.map(msg => 
              payload.messageIds.includes(msg.id)
                ? { ...msg, isRead: true, readAt: payload.readAt || new Date().toISOString() }
                : msg
            )
          );
        })
        // Fallback: postgres_changes for messages sent from other entry points
        // (FloatingChatBubble mini-chat, etc.) that don't have broadcast access
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'messages',
            filter: `conversation_id=eq.${conversationId}`
          },
          (payload) => {
            if (!isActive) return;
            const newRow = payload.new;
            if (!newRow?.id) return;
            
            // Format as the same shape the broadcast handler expects
            const sender = resolveSenderProfile(newRow.sender_id);
            handleNewMessage({
              id: newRow.id,
              senderId: newRow.sender_id,
              recipientId: newRow.recipient_id,
              senderName: sender.name,
              senderAvatar: sender.avatar,
              content: newRow.content,
              timestamp: newRow.sent_at,
              type: newRow.message_type || 'text',
              isRead: newRow.is_read,
              readAt: newRow.read_at || null
            });
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'messages',
            filter: `conversation_id=eq.${conversationId}`
          },
          (payload) => {
            if (!isActive) return;
            setMessages(prevMessages => 
              prevMessages.map(msg => 
                msg.id === payload.new.id 
                  ? { ...msg, isRead: payload.new.is_read, readAt: payload.new.read_at || null }
                  : msg
              )
            );
          }
        )
        .subscribe((status) => {
          if (!isActive) return;
          
          if (status === 'SUBSCRIBED') {
            isSubscribedRef.current = true;
            
            // On reconnection (not initial subscribe), sync messages to recover any
            // events lost during the WebSocket gap.
            if (hasSubscribedOnceRef.current) {
              silentSyncMessages(conversationId);
            }
            hasSubscribedOnceRef.current = true;
            reconnectAttemptsRef.current = 0;
          } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
            isSubscribedRef.current = false;
            // Exponential backoff reconnection: 1s, 2s, 4s, 8s, max 10s
            const attempt = reconnectAttemptsRef.current;
            if (isActive && attempt < 10) {
              const delay = Math.min(Math.pow(2, attempt) * 1000, 10000);
              reconnectAttemptsRef.current = attempt + 1;
              setTimeout(() => {
                if (isActive && messageChannel) {
                  messageChannel.subscribe();
                }
              }, delay);
            }
          }
        });
      
      broadcastChannelRef.current = messageChannel;
    }
    
    // Cleanup subscription when conversation changes or component unmounts
    return () => {
      isActive = false;
      isSubscribedRef.current = false;
      reconnectAttemptsRef.current = 0;

      // Send stop-typing before leaving (best-effort, channel may already be closing)
      if (isTypingSentRef.current && messageChannel) {
        messageChannel.send({
          type: 'broadcast',
          event: 'typing',
          payload: { userId: user?.id, isTyping: false }
        }).catch(() => {});
        isTypingSentRef.current = false;
      }
      if (typingSendTimeoutRef.current) { clearTimeout(typingSendTimeoutRef.current); typingSendTimeoutRef.current = null; }
      if (typingReceiveTimeoutRef.current) { clearTimeout(typingReceiveTimeoutRef.current); typingReceiveTimeoutRef.current = null; }

      broadcastChannelRef.current = null;
      
      if (postSendSyncTimerRef.current) {
        clearTimeout(postSendSyncTimerRef.current);
        postSendSyncTimerRef.current = null;
      }
      
      if (messageChannel) {
        messageChannel.unsubscribe();
        supabase.removeChannel(messageChannel);
      }
    };
  }, [selectedConversation?.conversation_id, user?.id, resolveSenderProfile, silentSyncMessages]);

  // Enhanced scroll management
  const handleScroll = useCallback((e) => {
    const container = e.target;
    const { scrollTop, scrollHeight, clientHeight } = container;
    
    // Clear existing timeout
    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current);
    }
    
    // Debounce scroll handling for performance
    scrollTimeout.current = setTimeout(() => {
      const isAtBottom = scrollHeight - scrollTop <= clientHeight + 100; // 100px threshold
      const isAtTop = scrollTop < 50; // 50px from top
      
      setIsNearBottom(isAtBottom);
      setShowScrollToBottom(!isAtBottom && messages.length > 0);
      
      // Auto-load older messages when scrolling to top (infinite scroll)
      if (isAtTop && !isLoadingOlderMessages && messages.length >= visibleMessageCount) {
        loadOlderMessages();
      }
      
      lastScrollTop.current = scrollTop;
    }, 100);
  }, [messages.length, visibleMessageCount, isLoadingOlderMessages, loadOlderMessages]);

  // Optimized message rendering with pagination
  const visibleMessages = useMemo(() => {
    if (messageViewMode === 'all') {
      return messages;
    }
    
    // Show most recent messages first, then load older ones
    const totalMessages = messages.length;
    const startIndex = Math.max(0, totalMessages - visibleMessageCount);
    
    return messages.slice(startIndex);
  }, [messages, visibleMessageCount, messageViewMode]);

  // Message search functionality
  const messageSearchResults = useMemo(() => {
    if (!messageSearchQuery.trim()) {
      return [];
    }
    
    const query = messageSearchQuery.toLowerCase();
    return messages
      .map((message, index) => ({
        message,
        index,
        content: typeof message.content === 'string' ? message.content : '',
        matches: typeof message.content === 'string' && message.content.toLowerCase().includes(query)
      }))
      .filter(result => result.matches);
  }, [messages, messageSearchQuery]);

  // Get current search result and scroll to it
  const currentSearchResult = useMemo(() => {
    if (messageSearchResults.length === 0) return null;
    return messageSearchResults[messageSearchResultIndex] || null;
  }, [messageSearchResults, messageSearchResultIndex]);

  // Handle search navigation
  const goToNextSearchResult = useCallback(() => {
    if (messageSearchResults.length === 0) return;
    setMessageSearchResultIndex((prev) => 
      (prev + 1) % messageSearchResults.length
    );
  }, [messageSearchResults]);

  const goToPreviousSearchResult = useCallback(() => {
    if (messageSearchResults.length === 0) return;
    setMessageSearchResultIndex((prev) => 
      prev === 0 ? messageSearchResults.length - 1 : prev - 1
    );
  }, [messageSearchResults]);

  // Scroll to search result
  useEffect(() => {
    if (currentSearchResult && messageContainerRef.current) {
      const messageElement = messageContainerRef.current.querySelector(
        `[data-message-id="${currentSearchResult.message.id}"]`
      );
      if (messageElement) {
        messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [currentSearchResult]);

  // Reset search result index when search query changes
  useEffect(() => {
    setMessageSearchResultIndex(0);
  }, [messageSearchQuery]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Force scroll to bottom function
  const forceScrollToBottom = () => {
    setIsNearBottom(true);
    scrollToBottom();
  };

  // Handle conversation selection (stable callback for memoized ConversationListItem)
  const handleConversationSelect = useCallback((conversation) => {
    selectConversation(conversation);
    setIsMobileView(true);
    
    // Mark all messages in this conversation as read
    if (conversation?.conversation_id) {
      markConversationAsRead(conversation.conversation_id);
    }
    
    // Don't update the URL with conversation ID to keep URLs clean
    if (window.location.pathname !== '/messages') {
      navigate('/messages');
    }
  }, [selectConversation, markConversationAsRead, navigate]);

  // Handle closing the message thread (mobile view)
  const handleCloseThread = () => {
    setIsMobileView(false);
    navigate('/messages');
  };

  // Handle sending a message with optimistic UI update
  const handleSendMessage = async (e) => {
    e?.preventDefault();
    
    if (!newMessage.trim() || !selectedConversation || !user?.id) {
      return;
    }

    const messageContent = newMessage.trim();
    const conversationId = selectedConversation.conversation_id || selectedConversation.id;
    
    if (!conversationId) {
      return;
    }

    // Clear input immediately for better UX
    setNewMessage('');
    stopTyping();

    // Mark the user as actively chatting — suppresses "seen" read receipts
    isActivelyChatting.current = true;
    if (activeChatCooldownRef.current) clearTimeout(activeChatCooldownRef.current);
    activeChatCooldownRef.current = setTimeout(() => {
      isActivelyChatting.current = false;
      // Flush any deferred read mark now that the conversation has cooled down
      flushPendingRead();
    }, 30000); // 30 seconds of no sends → resume read receipts
    
    // Close templates panel if open
    if (showTemplates) {
      setShowTemplates(false);
    }

    // Track whether the DB insert succeeded so the catch block doesn't
    // restore the input text when the message was actually sent.
    let sendSuccessful = false;

    try {
      // Get recipient ID from conversation data
      let recipientId = selectedConversation.participant_1 === user.id 
        ? selectedConversation.participant_2 
        : selectedConversation.participant_1;

      // If participant IDs are not available, fetch from database
      if (!recipientId) {
        const { data: convData, error: convError } = await supabase
          .from('conversations')
          .select('participant_1, participant_2')
          .eq('id', conversationId)
          .single();
        
        if (convError || !convData) {
          setNewMessage(messageContent); // Restore message
          return;
        }
        
        recipientId = convData.participant_1 === user.id 
          ? convData.participant_2 
          : convData.participant_1;
      }

      if (!recipientId) {
        setNewMessage(messageContent); // Restore message
        return;
      }

      // Send with optimistic update - message appears instantly
      const result = await sendMessageOptimistic(conversationId, messageContent, recipientId);

      if (!result.success) {
        // Restore message to input on failure
        setNewMessage(messageContent);
        return;
      }

      // Message is in DB and UI — don't restore input even if something below throws
      sendSuccessful = true;

      // Mark the real message ID as processed so the postgres_changes fallback
      // doesn't double-deliver our own outgoing message after the DB insert fires.
      if (result.data?.id) {
        processedMessageIds.current.add(result.data.id);
      }

      // Broadcast the message to the other participant via the shared channel.
      // This is the primary real-time delivery mechanism — both users subscribe
      // to the same `chat:${conversationId}` broadcast channel.
      if (result.data && broadcastChannelRef.current) {
        const sender = resolveSenderProfile(user.id);
        broadcastChannelRef.current.send({
          type: 'broadcast',
          event: 'new_message',
          payload: {
            id: result.data.id,
            senderId: user.id,
            recipientId: recipientId,
            senderName: sender.name,
            senderAvatar: sender.avatar,
            content: result.data.content,
            timestamp: result.data.timestamp,
            type: result.data.type || 'text',
            isRead: false,
            readAt: null
          }
        }).catch(() => {
          // Broadcast failure is non-fatal — the recipient will receive the
          // message via the postgres_changes INSERT fallback subscription.
        });
      }

      // Auto-scroll to bottom
      setIsNearBottom(true);
      setTimeout(() => {
        scrollToBottom();
      }, 50);

      // Post-send reconciliation: after a delay, silently refetch to confirm
      // delivery and catch any messages the realtime channel may have missed.
      // Delay is 3s (not 1.5s) to ensure the DB INSERT has propagated before
      // the query runs — avoids silently removing the optimistic message on
      // slow connections where the insert hasn't committed yet.
      if (postSendSyncTimerRef.current) clearTimeout(postSendSyncTimerRef.current);
      postSendSyncTimerRef.current = setTimeout(() => {
        silentSyncMessages(conversationId);
      }, 3000);

    } catch (error) {
      // Only restore the message to input if the DB insert never succeeded.
      // If sendSuccessful is true the message is already in the DB and local
      // state — restoring the input would cause a confusing duplicate send.
      if (!sendSuccessful) {
        setNewMessage(messageContent);
      }
    }
  };


  
  // Handle template selection
  const handleTemplateSelect = async (template) => {
    setNewMessage(template.content);
    setShowTemplates(false);
    
    // Increment usage count for custom templates
    if (!template.isDefault && template.id && user?.id) {
      try {
        await messageTemplateService.incrementUsageCount(template.id, user.id);
        await loadCustomTemplates(); // Refresh to update usage count
      } catch (error) {
        // Error logged via ErrorHandler
        // Don't show error to user as template content is still applied
      }
    }
  };

  // Handle template selection from the template manager
  const handleTemplateSelectFromManager = async (template) => {
    setNewMessage(template.content);
    setShowTemplateManager(false);
    
    // Increment usage count for custom templates
    if (!template.isDefault && template.id && user?.id) {
      try {
        await messageTemplateService.incrementUsageCount(template.id, user.id);
        await loadCustomTemplates(); // Refresh to update usage count
      } catch (error) {
        // Error logged via ErrorHandler
      }
    }
  };

  // Handle conversation deletion (soft delete - user-specific)
  const handleDeleteConversation = async () => {
    if (!selectedConversation?.conversation_id) {
      // Error logged via ErrorHandler
      return;
    }

    setIsDeleting(true);

    try {
      const conversationId = selectedConversation.conversation_id;

      // Soft delete using atomic RPC operation
      // Single atomic UPDATE with array_append and NOT (user_id = ANY(deleted_by)) guard
      // This prevents race conditions and ensures idempotent deletions
      const { data, error: rpcError } = await supabase
        .rpc('soft_delete_conversation', {
          conversation_id_param: conversationId,
          user_id_param: user?.id
        });

      if (rpcError) {
        throw new Error(`Failed to delete conversation: ${rpcError.message}`);
      }

      // Handle RPC response
      const result = data && data.length > 0 ? data[0] : null;
      if (!result?.success) {
        // RPC returns success: false with specific error message
        throw new Error(result?.message || 'Failed to delete conversation');
      }

      // Close the dialog
      setShowDeleteDialog(false);

      // Clear selected conversation
      selectConversation(null);

      // Refresh conversations list for accuracy (will exclude deleted conversation)
      await loadConversations(true);

      // Navigate back to conversations list (mobile view)
      setIsMobileView(false);
      navigate('/messages');
    } catch (error) {
      // Error logged via ErrorHandler
      ErrorHandler.logError('UnifiedMessagingPage', 'Failed to delete conversation', error);
    } finally {
      setIsDeleting(false);
    }
  };


  // Handle quick action button click
  const handleQuickActionClick = (actionType) => {
    setQuickActionType(actionType);
    setShowQuickActionModal(true);
  };

  // Handle quick action submission
  const handleQuickActionSubmit = async (formData) => {
    if (!selectedConversation || !user?.id) return;

    try {
      const recipientId = selectedConversation.participant_1 === user.id 
        ? selectedConversation.participant_2 
        : selectedConversation.participant_1;

      let messageContent = '';

      switch (quickActionType) {
        case 'interview':
          messageContent = `📅 **Interview Scheduled**\n\n` +
            `Date: ${formData.date}\n` +
            `Time: ${formData.time}\n` +
            `Location: ${formData.location || 'Virtual'}\n` +
            (formData.notes ? `\nNotes:\n${formData.notes}` : '');
          break;

        case 'portfolio':
          messageContent = `Thank you for your interest! Could you please share your portfolio or some examples of your work? I'd love to see what you've done.` +
            (formData.notes ? `\n\n${formData.notes}` : '');
          break;

        case 'offer':
          messageContent = `Great news! I'd like to extend a job offer for the ${selectedConversation.job_title || 'position'}.\n\n` +
            `**Offer Details:**\n${formData.offerDetails}`;
          break;

        case 'ask':
          messageContent = `I have a few questions about the role:\n\n${formData.questions}`;
          break;

        case 'confirm':
          messageContent = `I wanted to confirm my availability for this position:\n\n${formData.availability}`;
          break;

        case 'share_portfolio':
          messageContent = `I'd like to share my portfolio with you:\n\n${formData.portfolioLink}` +
            (formData.notes ? `\n\nAdditional links:\n${formData.notes}` : '');
          break;

        default:
          return;
      }

      // Send the message (sendMessage derives recipientId internally from selectedConversation)
      const quickResult = await sendMessage(
        selectedConversation.conversation_id || selectedConversation.id,
        messageContent
      );

      // Broadcast via shared channel so the other participant receives it
      if (quickResult?.success && quickResult.data && broadcastChannelRef.current) {
        const sender = resolveSenderProfile(user.id);
        broadcastChannelRef.current.send({
          type: 'broadcast',
          event: 'new_message',
          payload: {
            id: quickResult.data.id,
            senderId: user.id,
            recipientId: recipientId,
            senderName: sender.name,
            senderAvatar: sender.avatar,
            content: quickResult.data.content,
            timestamp: quickResult.data.timestamp,
            type: quickResult.data.type || 'text',
            isRead: false,
            readAt: null
          }
        });
      }

      // Show success feedback
      // Debug info removed for production
    } catch (error) {
      // Error logged via ErrorHandler
      ErrorHandler.logError('UnifiedMessagingPage', `Failed to send quick action: ${quickActionType}`, error);
    }
  };

  // Memoized filtered conversations — avoids re-filtering on every render
  const filteredConversations = useMemo(() => {
    if (!conversations || conversations.length === 0) {
      return [];
    }

    return conversations
      .filter(conv => conv && conv.conversation_id)
      .filter(conv => {
        let searchMatch = true;
        
        if (searchQuery && searchQuery.trim()) {
          const lowerQuery = searchQuery.toLowerCase().trim();
          
          searchMatch = 
            (conv.participant_name && conv.participant_name.toLowerCase().includes(lowerQuery)) ||
            (conv.job_context && conv.job_context.toLowerCase().includes(lowerQuery)) ||
            (conv.job_title && conv.job_title.toLowerCase().includes(lowerQuery)) ||
            (conv.company_name && conv.company_name.toLowerCase().includes(lowerQuery));
        }
        
        let statusMatch = true;
        if (filter === 'unread') {
          statusMatch = conv.unread_count > 0;
        } else if (filter === 'employers' && !isEmployer) {
          statusMatch = (conv.recipient_role || conv.participant_role) === 'employer';
        } else if (filter === 'candidates' && isEmployer) {
          statusMatch = (conv.recipient_role || conv.participant_role) === 'va';
        }
        
        return searchMatch && statusMatch;
      });
  }, [conversations, searchQuery, filter, isEmployer]);

  // Memoized tab definitions — avoids recreating objects and re-filtering on every render
  const tabFilters = useMemo(() => {
    const roleKey = isEmployer ? 'candidates' : 'employers';
    const roleLabel = isEmployer ? 'Candidates' : 'Employers';
    const roleCount = conversations.filter(c => {
      const roleToCheck = c.recipient_role || c.participant_role;
      return isEmployer ? roleToCheck === 'va' : roleToCheck === 'employer';
    }).length;

    return [
      { key: 'all', label: 'All', count: conversations.length },
      { key: 'unread', label: 'Unread', count: unreadCount },
      { key: roleKey, label: roleLabel, count: roleCount }
    ];
  }, [conversations, unreadCount, isEmployer]);


  return (
    <div className="min-h-screen bg-gray-50">
      {isEmployer ? <EmployerHeaderNav /> : <VAHeaderNav />}
      <main className="w-full max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden" style={{ minHeight: '70vh' }}>
          <div className="flex h-full flex-col lg:flex-row">
            {/* Conversations List - Left Panel */}
            <div className={`${
              isMobileView ? 'hidden lg:block' : 'block'
            } w-full lg:w-1/3 lg:border-r border-gray-200 flex flex-col bg-white lg:bg-transparent`}>
              {/* Search and Filter */}
              <div className="p-3 sm:p-4 border-b border-gray-200">
                <Input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="mb-3 sm:mb-4"
                  iconName="Search"
                />
                
                <div className="flex bg-gray-100 rounded-lg p-0.5 sm:p-1 overflow-x-auto">
                  {tabFilters.map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => setFilter(tab.key)}
                      className={`flex-1 min-w-0 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                        filter === tab.key
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <span className="truncate">{tab.label}</span>
                      {tab.count > 0 && (
                        <span className="ml-0.5 sm:ml-1 text-xs">({tab.count})</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Conversations List */}
              <div className="flex-1 overflow-y-auto">
                {isLoadingConversations ? (
                  <div className="space-y-2 animate-pulse p-3 sm:p-4">
                    {/* Skeleton for conversation item 1 */}
                    <div className="flex items-start space-x-3 p-3 sm:p-4">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-300 rounded-full flex-shrink-0"></div>
                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="flex justify-between items-start">
                          <div className="h-4 bg-gray-300 rounded w-24"></div>
                          <div className="h-3 bg-gray-300 rounded w-12"></div>
                        </div>
                        <div className="h-3 bg-gray-200 rounded w-32"></div>
                        <div className="h-3 bg-gray-200 rounded w-40"></div>
                      </div>
                    </div>
                    
                    {/* Skeleton for conversation item 2 */}
                    <div className="flex items-start space-x-3 p-3 sm:p-4 border-t border-gray-100">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-300 rounded-full flex-shrink-0"></div>
                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="flex justify-between items-start">
                          <div className="h-4 bg-gray-300 rounded w-28"></div>
                          <div className="h-3 bg-gray-300 rounded w-12"></div>
                        </div>
                        <div className="h-3 bg-gray-200 rounded w-36"></div>
                        <div className="h-3 bg-gray-200 rounded w-44"></div>
                      </div>
                    </div>
                    
                    {/* Skeleton for conversation item 3 */}
                    <div className="flex items-start space-x-3 p-3 sm:p-4 border-t border-gray-100">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-300 rounded-full flex-shrink-0"></div>
                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="flex justify-between items-start">
                          <div className="h-4 bg-gray-300 rounded w-20"></div>
                          <div className="h-3 bg-gray-300 rounded w-12"></div>
                        </div>
                        <div className="h-3 bg-gray-200 rounded w-40"></div>
                        <div className="h-3 bg-gray-200 rounded w-32"></div>
                      </div>
                    </div>
                    
                    {/* Skeleton for conversation item 4 */}
                    <div className="flex items-start space-x-3 p-3 sm:p-4 border-t border-gray-100">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-300 rounded-full flex-shrink-0"></div>
                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="flex justify-between items-start">
                          <div className="h-4 bg-gray-300 rounded w-24"></div>
                          <div className="h-3 bg-gray-300 rounded w-12"></div>
                        </div>
                        <div className="h-3 bg-gray-200 rounded w-36"></div>
                        <div className="h-3 bg-gray-200 rounded w-40"></div>
                      </div>
                    </div>
                  </div>
                ) : filteredConversations.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-48 sm:h-64 text-gray-500 p-4">
                    <Icon name="MessageSquare" size={48} className="sm:w-16 sm:h-16 text-gray-300 mb-3 sm:mb-4" />
                    <p className="text-base sm:text-lg font-medium text-center">No conversations yet</p>
                    <p className="text-xs sm:text-sm text-center mt-1">
                      {isEmployer 
                        ? "Start messaging candidates to connect with them!" 
                        : "Start applying to jobs to connect with employers!"}
                    </p>
                  </div>
                ) : (
                  <div className="h-full flex flex-col divide-y divide-gray-200">
                    {/* Conversation count indicator */}
                    {conversationPagination.totalCount > 0 && !searchQuery.trim() && filter === 'all' && (
                      <div className="px-3 sm:px-4 py-2 bg-gray-50 border-b border-gray-100 flex-shrink-0">
                        <p className="text-xs text-gray-500">
                          Showing {conversations.length} of {conversationPagination.totalCount} conversations
                        </p>
                      </div>
                    )}                    
                    <div className="flex-1 overflow-y-auto">
                      {filteredConversations.map((conversation) => {
                        if (!conversation?.conversation_id) return null;
                        
                        return (
                          <ConversationListItem 
                            key={conversation.conversation_id} 
                            conversation={conversation}
                            isActive={selectedConversation?.conversation_id === conversation.conversation_id}
                            currentUserId={user?.id}
                            isEmployer={isEmployer}
                            onSelect={handleConversationSelect}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Pagination Controls - outside filteredConversations check, still accessible during empty state */}
                {!isLoadingConversations && (conversationPagination.hasMore || (conversationPagination.currentPage && conversationPagination.currentPage > 1)) && (
                  <div className="p-3 sm:p-4 border-t border-gray-100 flex-shrink-0">
                    <div className="flex items-center justify-between gap-2 sm:gap-3">
                      <button
                        onClick={() => loadMoreConversations(-1)}
                        disabled={!conversationPagination.currentPage || conversationPagination.currentPage <= 1 || conversationPagination.isLoadingMore}
                        className="flex-1 h-9 sm:h-10 px-2 sm:px-3 text-xs sm:text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 
                                   border border-gray-300 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-50
                                   flex items-center justify-center space-x-1"
                        title="Previous page"
                      >
                        <Icon name="ChevronUp" size={14} />
                        <span className="hidden sm:inline">Previous</span>
                      </button>
                      
                      <div className="flex-1 text-center">
                        <p className="text-xs sm:text-sm text-gray-600">
                          <span className="font-medium text-gray-900">
                            {conversationPagination.currentPage || 1}
                          </span>
                          <span className="mx-1">of</span>
                          <span className="font-medium text-gray-900">
                            {Math.ceil((conversationPagination.totalCount || 0) / 6) || 1}
                          </span>
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {conversations.length} shown
                        </p>
                      </div>
                      
                      <button
                        onClick={() => loadMoreConversations(1)}
                        disabled={!conversationPagination.hasMore || conversationPagination.isLoadingMore}
                        className="flex-1 h-9 sm:h-10 px-2 sm:px-3 text-xs sm:text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 
                                   border border-gray-300 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-50
                                   flex items-center justify-center space-x-1"
                        title="Next page"
                      >
                        {conversationPagination.isLoadingMore ? (
                          <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-gray-700"></div>
                        ) : (
                          <>
                            <span className="hidden sm:inline">Next</span>
                            <Icon name="ChevronDown" size={14} />
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Message Thread - Right Panel */}
            <div className={`${
              isMobileView ? 'block' : 'hidden'
            } lg:block flex-1 flex flex-col bg-white lg:bg-transparent`}>
              {selectedConversation ? (
                <>
                  {/* Conversation Header */}
                  <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200">
                    <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
                      <button
                        className="lg:hidden p-1.5 sm:p-2 text-gray-500 hover:text-gray-700 active:text-gray-800"
                        onClick={handleCloseThread}
                      >
                        <Icon name="ArrowLeft" size={18} />
                      </button>
                      <ConversationAvatar 
                        profilePicture={selectedConversation.participant_avatar} 
                        name={selectedConversation.participant_name}
                        userId={selectedConversation.participant_1 === user?.id ? selectedConversation.participant_2 : selectedConversation.participant_1}
                        size="w-8 h-8 sm:w-10 sm:h-10"
                        showOnlineStatus={true}
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm sm:text-base text-gray-900 truncate">{selectedConversation.participant_name}</h3>
                        <p className="text-xs sm:text-sm text-gray-500 truncate">
                          {isEmployer 
                            ? selectedConversation.job_context
                            : selectedConversation.company_name ? `${selectedConversation.company_name} • ${selectedConversation.job_title || ''}` : selectedConversation.job_title
                          }
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowMessageSearch(!showMessageSearch)}
                        className="h-8 sm:h-auto text-xs sm:text-sm px-2 sm:px-3"
                        title="Search messages"
                      >
                        <Icon name="Search" size={14} className="sm:mr-1" />
                        <span className="hidden sm:inline">Search</span>
                      </Button>
                      {isEmployer && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowInterviewModal(true)}
                          className="h-8 sm:h-auto text-xs sm:text-sm px-2 sm:px-3"
                        >
                          <Icon name="Calendar" size={14} className="mr-0.5 sm:mr-1" />
                          <span className="hidden sm:inline">Schedule Interview</span>
                          <span className="sm:hidden">Interview</span>
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowDeleteDialog(true)}
                        className="h-8 sm:h-auto text-xs sm:text-sm px-2 sm:px-3 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                      >
                        <Icon name="Trash2" size={14} className="sm:mr-1" />
                        <span className="hidden sm:inline">Delete</span>
                      </Button>
                    </div>
                  </div>

                  {/* Message Search Bar */}
                  {showMessageSearch && (
                    <div className="px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-200 bg-blue-50">
                      <div className="flex items-center space-x-2">
                        <Input
                          type="text"
                          placeholder="Search messages..."
                          value={messageSearchQuery}
                          onChange={(e) => setMessageSearchQuery(e.target.value)}
                          className="flex-1"
                          autoFocus
                        />
                        <button
                          onClick={goToPreviousSearchResult}
                          disabled={messageSearchResults.length === 0}
                          className="p-2 text-gray-600 hover:text-gray-800 hover:bg-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Previous result"
                        >
                          <Icon name="ChevronUp" size={16} />
                        </button>
                        <button
                          onClick={goToNextSearchResult}
                          disabled={messageSearchResults.length === 0}
                          className="p-2 text-gray-600 hover:text-gray-800 hover:bg-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Next result"
                        >
                          <Icon name="ChevronDown" size={16} />
                        </button>
                        <button
                          onClick={() => {
                            setShowMessageSearch(false);
                            setMessageSearchQuery('');
                            setMessageSearchResultIndex(-1);
                          }}
                          className="p-2 text-gray-600 hover:text-gray-800 hover:bg-white rounded transition-colors"
                          title="Close search"
                        >
                          <Icon name="X" size={16} />
                        </button>
                      </div>
                      {messageSearchQuery.trim() && (
                        <div className="mt-2 text-xs text-gray-600">
                          {messageSearchResults.length === 0 ? (
                            <span>No results found</span>
                          ) : (
                            <span>
                              {messageSearchResultIndex + 1} of {messageSearchResults.length} result{messageSearchResults.length !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Messages */}
                  <div className="flex-1 flex flex-col relative">
                    {/* Message view mode controls */}
                    {messages.length > 30 && (
                      <div className="px-3 sm:px-4 py-2 border-b border-gray-100 bg-gray-50">
                        <div className="flex items-center justify-between text-xs sm:text-sm">
                          <div className="flex items-center space-x-2 sm:space-x-4 flex-1 min-w-0">
                            <span className="text-gray-600 hidden sm:inline">View:</span>
                            <div className="flex space-x-1 sm:space-x-2 overflow-x-auto">
                              <button
                                onClick={() => setMessageViewMode('recent')}
                                className={`px-2 py-1 rounded text-xs whitespace-nowrap ${
                                  messageViewMode === 'recent'
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'text-gray-600 hover:text-gray-800'
                                }`}
                              >
                                Recent ({Math.min(50, messages.length)})
                              </button>
                              <button
                                onClick={() => setMessageViewMode('all')}
                                className={`px-2 py-1 rounded text-xs whitespace-nowrap ${
                                  messageViewMode === 'all'
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'text-gray-600 hover:text-gray-800'
                                }`}
                              >
                                All ({messages.length})
                              </button>
                            </div>
                          </div>
                          <div className="text-gray-500 text-xs whitespace-nowrap ml-2">
                            <span className="hidden sm:inline">{visibleMessages.length} of {messages.length} messages</span>
                            <span className="sm:hidden">{visibleMessages.length}/{messages.length}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Messages container with enhanced scrolling */}
                    <div 
                      ref={messageContainerRef}
                      className="flex-1 max-h-96 min-h-[300px] overflow-y-auto p-3 sm:p-4 scroll-smooth"
                      onScroll={handleScroll}
                      style={{ 
                        scrollBehavior: 'smooth',
                        overflowAnchor: 'auto' // Prevents scroll jumping when new content loads
                      }}
                    >
                      {isLoadingMessages ? (
                        <div className="space-y-4 animate-pulse">
                          {/* Skeleton for incoming message */}
                          <div className="flex justify-start mb-4">
                            <div className="w-8 h-8 bg-gray-300 rounded-full flex-shrink-0"></div>
                            <div className="ml-2 max-w-xs">
                              <div className="bg-gray-200 rounded-lg rounded-bl-sm p-3 h-16 w-48"></div>
                              <div className="mt-1 h-3 w-24 bg-gray-200 rounded"></div>
                            </div>
                          </div>
                          
                          {/* Skeleton for outgoing message */}
                          <div className="flex justify-end mb-4">
                            <div className="max-w-xs">
                              <div className="bg-blue-200 rounded-lg rounded-br-sm p-3 h-20 w-56"></div>
                              <div className="mt-1 h-3 w-20 bg-gray-200 rounded ml-auto"></div>
                            </div>
                          </div>
                          
                          {/* Skeleton for incoming message */}
                          <div className="flex justify-start mb-4">
                            <div className="w-8 h-8 bg-gray-300 rounded-full flex-shrink-0"></div>
                            <div className="ml-2 max-w-xs">
                              <div className="bg-gray-200 rounded-lg rounded-bl-sm p-3 h-12 w-40"></div>
                              <div className="mt-1 h-3 w-24 bg-gray-200 rounded"></div>
                            </div>
                          </div>
                          
                          {/* Skeleton for outgoing message */}
                          <div className="flex justify-end mb-4">
                            <div className="max-w-xs">
                              <div className="bg-blue-200 rounded-lg rounded-br-sm p-3 h-16 w-52"></div>
                              <div className="mt-1 h-3 w-20 bg-gray-200 rounded ml-auto"></div>
                            </div>
                          </div>
                          
                          {/* Skeleton for incoming message */}
                          <div className="flex justify-start mb-4">
                            <div className="w-8 h-8 bg-gray-300 rounded-full flex-shrink-0"></div>
                            <div className="ml-2 max-w-xs">
                              <div className="bg-gray-200 rounded-lg rounded-bl-sm p-3 h-14 w-44"></div>
                              <div className="mt-1 h-3 w-24 bg-gray-200 rounded"></div>
                            </div>
                          </div>
                        </div>
                      ) : messages.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-48 sm:h-64 text-gray-500 p-4">
                          <Icon name="MessageSquare" size={48} className="sm:w-16 sm:h-16 text-gray-300 mb-3 sm:mb-4" />
                          <p className="text-base sm:text-lg font-medium text-center">No messages yet</p>
                          <p className="text-xs sm:text-sm text-center mt-1">Send a message to start the conversation!</p>
                        </div>
                      ) : (
                        <>
                          {/* Load older messages indicator */}
                          {visibleMessages.length < messages.length && (
                            <div className="text-center py-4">
                              {isLoadingOlderMessages ? (
                                <div className="flex items-center justify-center space-x-2">
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                  <span className="text-sm text-gray-500">Loading older messages...</span>
                                </div>
                              ) : (
                                <button
                                  onClick={loadOlderMessages}
                                  className="text-sm text-blue-600 hover:text-blue-800 transition-colors px-3 py-1 rounded-md hover:bg-blue-50"
                                >
                                  Load {Math.min(50, messages.length - visibleMessages.length)} older messages
                                </button>
                              )}
                            </div>
                          )}

                          {/* Debug info */}
                          {process.env.NODE_ENV === 'development' && (
                            <div className="text-xs text-gray-400 mb-2">
                              Rendering {visibleMessages.length} messages (Total: {messages.length})
                            </div>
                          )}

                          {/* Render visible messages */}
                          {visibleMessages.map((message) => (
                            <ChatMessageBubble
                              key={message.id}
                              message={message}
                              currentUserId={user?.id}
                              isSearchResult={currentSearchResult?.message?.id === message.id}
                              searchQuery={messageSearchQuery}
                            />
                          ))}
                        </>
                      )}
                      {/* Typing indicator */}
                      <TypingIndicator isTyping={isRecipientTyping} recipientName={typingUserName} />
                      <div ref={messagesEndRef} />
                    </div>

                    {/* Scroll to bottom button */}
                    {showScrollToBottom && (
                      <div className="absolute bottom-3 sm:bottom-4 right-3 sm:right-4 z-10">
                        <button
                          onClick={forceScrollToBottom}
                          className="text-white p-2 sm:p-2.5 rounded-full shadow-lg transition-all duration-200 transform hover:scale-110 active:scale-95 active:opacity-80"
                          style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' }}
                          title="Scroll to bottom"
                        >
                          <Icon name="ChevronDown" size={14} />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Message Input */}
                  <div className="p-3 sm:p-4 border-t border-gray-200">
                    <form onSubmit={handleSendMessage} className="flex flex-col space-y-2 sm:space-y-3">
                      <div className="flex items-center space-x-1 sm:space-x-2 mb-1 sm:mb-2 overflow-x-auto">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setShowTemplates(!showTemplates)}
                          className="h-8 sm:h-auto text-xs sm:text-sm px-2 sm:px-3 whitespace-nowrap"
                        >
                          <Icon name="FileText" size={14} className="mr-0.5 sm:mr-1" />
                          <span className="hidden sm:inline">Quick Templates</span>
                          <span className="sm:hidden">Quick</span>
                        </Button>
                        
                        {/* <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setShowTemplateManager(true)}
                          className="h-8 sm:h-auto text-xs sm:text-sm px-2 sm:px-3 whitespace-nowrap"
                        >
                          <Icon name="Settings" size={14} className="mr-0.5 sm:mr-1" />
                          <span className="hidden sm:inline">Manage Templates</span>
                          <span className="sm:hidden">Manage</span>
                          {customTemplates.length > 0 && (
                            <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-600 text-xs rounded-full">
                              {customTemplates.length}
                            </span>
                          )}
                        </Button> */}
                      </div>
                      
                      {showTemplates && (
                        <div className="mb-2 sm:mb-3 p-2 sm:p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium text-sm sm:text-base text-gray-900">Message Templates</h4>
                            <button
                              type="button"
                              onClick={() => {
                                setShowTemplates(false);
                                setShowTemplateManager(true);
                              }}
                              className="text-xs text-blue-600 hover:text-blue-800 transition-colors"
                            >
                              Manage
                            </button>
                          </div>
                          
                          {isLoadingTemplates ? (
                            <div className="flex items-center justify-center py-4">
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                              <span className="ml-2 text-sm text-gray-600">Loading...</span>
                            </div>
                          ) : (
                            <div className="space-y-0.5 sm:space-y-1 max-h-48 overflow-y-auto">
                              {/* Show custom templates first */}
                              {customTemplates.length > 0 && (
                                <>
                                  <div className="text-xs font-semibold text-gray-500 px-2 py-1">
                                    Your Templates
                                  </div>
                                  {customTemplates.map(template => (
                                    <button
                                      key={`custom-${template.id}`}
                                      type="button"
                                      onClick={() => handleTemplateSelect(template)}
                                      className="block w-full text-left px-2 py-1.5 text-xs sm:text-sm text-gray-700 hover:bg-gray-100 rounded transition-colors group"
                                    >
                                      <div className="flex items-center justify-between">
                                        <span className="font-medium">{template.name}</span>
                                        {template.usage_count > 0 && (
                                          <span className="text-xs text-gray-400">
                                            {template.usage_count}×
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-xs text-gray-500 mt-0.5 overflow-hidden truncate">
                                        {template.content}
                                      </p>
                                    </button>
                                  ))}
                                </>
                              )}
                              
                              {/* Show default templates */}
                              {messageTemplates.length > 0 && (
                                <>
                                  <div className="text-xs font-semibold text-gray-500 px-2 py-1 mt-2">
                                    Quick Templates
                                  </div>
                                  {messageTemplates.map(template => (
                                    <button
                                      key={`default-${template.id}`}
                                      type="button"
                                      onClick={() => handleTemplateSelect({ ...template, isDefault: true })}
                                      className="block w-full text-left px-2 py-1 text-xs sm:text-sm text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                    >
                                      {template.name}
                                    </button>
                                  ))}
                                </>
                              )}
                              
                              {/* Empty state */}
                              {customTemplates.length === 0 && messageTemplates.length === 0 && (
                                <div className="text-center py-4 text-gray-500">
                                  <p className="text-sm">No templates available</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      <div className="flex items-end space-x-2 sm:space-x-3">
                        <div className="flex-1">
                          <textarea
                            value={newMessage}
                            onChange={(e) => {
                              setNewMessage(e.target.value);
                              if (e.target.value.trim()) broadcastTyping();
                              else stopTyping();
                            }}
                            placeholder="Type your message..."
                            rows={window.innerWidth < 640 ? 2 : 3}
                            className="w-full px-2 sm:px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSendMessage(e);
                              }
                            }}
                          />
                        </div>
                        
                        <Button
                          type="submit"
                          variant="primary"
                          disabled={!newMessage.trim()}
                          className="h-10 sm:h-auto px-3 sm:px-4"
                        >
                          <Icon name="Send" size={14} className="mr-0.5 sm:mr-1" />
                          <span className="text-xs sm:text-sm">Send</span>
                        </Button>
                      </div>
                    </form>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex h-full items-center justify-center p-4">
                  <div className="text-center max-w-md">
                    <Icon name="MessageSquare" size={48} className="sm:w-16 sm:h-16 text-gray-300 mx-auto mb-3 sm:mb-4" />
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Select a conversation</h3>
                    <p className="text-sm sm:text-base text-gray-600">Choose a conversation from the sidebar to start messaging.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Tips Section */}
        <div className="mt-4 sm:mt-6 lg:mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="MessageCircle" size={16} className="sm:w-5 sm:h-5 text-blue-600" />
              </div>
              <h3 className="font-semibold text-sm sm:text-base text-gray-900">Messaging Tips</h3>
            </div>
            <ul className="text-xs sm:text-sm text-gray-600 space-y-1 sm:space-y-2">
              <li>• Be professional and courteous</li>
              <li>• Respond promptly to messages</li>
              <li>• Ask relevant questions about the role</li>
              <li>• Share your availability clearly</li>
            </ul>
          </div>

          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="CheckCircle" size={16} className="sm:w-5 sm:h-5 text-green-600" />
              </div>
              <h3 className="font-semibold text-sm sm:text-base text-gray-900">Best Practices</h3>
            </div>
            <ul className="text-xs sm:text-sm text-gray-600 space-y-1 sm:space-y-2">
              <li>• Use proper grammar and spelling</li>
              <li>• Keep messages concise and clear</li>
              <li>• Follow up appropriately</li>
              <li>• Be honest about your skills and availability</li>
            </ul>
          </div>

          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200 sm:col-span-2 lg:col-span-1">
            <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="Zap" size={16} className="sm:w-5 sm:h-5 text-purple-600" />
              </div>
              <h3 className="font-semibold text-sm sm:text-base text-gray-900">Quick Actions</h3>
            </div>
            <div className="space-y-1 sm:space-y-2">
              {isEmployer ? (
                <>
                  <button 
                    onClick={() => handleQuickActionClick('interview')}
                    disabled={!selectedConversation}
                    className="w-full text-left text-xs sm:text-sm text-blue-600 hover:text-blue-800 active:text-blue-900 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors p-1 rounded hover:bg-blue-50"
                  >
                    → Schedule an interview
                  </button>
                  <button 
                    onClick={() => handleQuickActionClick('portfolio')}
                    disabled={!selectedConversation}
                    className="w-full text-left text-xs sm:text-sm text-blue-600 hover:text-blue-800 active:text-blue-900 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors p-1 rounded hover:bg-blue-50"
                  >
                    → Request portfolio
                  </button>
                  <button 
                    onClick={() => handleQuickActionClick('offer')}
                    disabled={!selectedConversation}
                    className="w-full text-left text-xs sm:text-sm text-blue-600 hover:text-blue-800 active:text-blue-900 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors p-1 rounded hover:bg-blue-50"
                  >
                    → Send job offer
                  </button>
                </>
              ) : (
                <>
                  <button 
                    onClick={() => handleQuickActionClick('share_portfolio')}
                    disabled={!selectedConversation}
                    className="w-full text-left text-xs sm:text-sm text-blue-600 hover:text-blue-800 active:text-blue-900 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors p-1 rounded hover:bg-blue-50"
                  >
                    → Share your portfolio
                  </button>
                  <button 
                    onClick={() => handleQuickActionClick('ask')}
                    disabled={!selectedConversation}
                    className="w-full text-left text-xs sm:text-sm text-blue-600 hover:text-blue-800 active:text-blue-900 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors p-1 rounded hover:bg-blue-50"
                  >
                    → Ask about the role
                  </button>
                  <button 
                    onClick={() => handleQuickActionClick('confirm')}
                    disabled={!selectedConversation}
                    className="w-full text-left text-xs sm:text-sm text-blue-600 hover:text-blue-800 active:text-blue-900 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors p-1 rounded hover:bg-blue-50"
                  >
                    → Confirm availability
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Delete Conversation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 animate-scale-in">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon name="AlertTriangle" size={24} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Conversation</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-3">
                Are you sure you want to delete this conversation with{' '}
                <span className="font-semibold text-gray-900">
                  {selectedConversation?.participant_name}
                </span>
                ?
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <ul className="text-sm text-red-800 space-y-1">
                  <li className="flex items-start">
                    <Icon name="Eye" size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                    <span>This conversation will be hidden from your list</span>
                  </li>
                  <li className="flex items-start">
                    <Icon name="Users" size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                    <span>The other person will still see the conversation on their end</span>
                  </li>
                  <li className="flex items-start">
                    <Icon name="RefreshCw" size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                    <span>You can still access it if the other person messages you</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteDialog(false)}
                disabled={isDeleting}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteConversation}
                disabled={isDeleting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Icon name="Trash2" size={16} className="mr-2" />
                    Delete Conversation
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Interview Schedule Modal */}
      {selectedConversation && (
        <EnhancedInterviewScheduleModal
          isOpen={showInterviewModal}
          onClose={() => setShowInterviewModal(false)}
          onSubmit={async (interviewData) => {
            try {
              const candidateId = selectedConversation.participant_1 === user?.id
                ? selectedConversation.participant_2
                : selectedConversation.participant_1;

              // Build the create payload
              const createPayload = {
                ...interviewData,
                candidate_id: interviewData.selectedApplication?.applicant_id || candidateId,
                application_id: interviewData.selectedApplication?.id || null,
                interviewer_id: user.id,
                status: 'scheduled',
                type: interviewData.interview_type || 'video',
                jobTitle: interviewData.job_title || selectedConversation.job_title,
                created_at: new Date().toISOString()
              };

              // Persist the interview to interview_schedules table
              const { interview, error: createError } = await supabaseService.interviews.createInterview(createPayload);
              if (createError) throw new Error(createError);

              // Send an interview notification message in the conversation
              const scheduledDate = new Date(interviewData.scheduled_at);
              const dateStr = scheduledDate.toLocaleDateString('en-US', {
                weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
              });
              const timeStr = scheduledDate.toLocaleTimeString('en-US', {
                hour: '2-digit', minute: '2-digit'
              });

              const interviewType = interviewData.interview_type || createPayload.type || 'video';
              const typeLabelMap = {
                video: 'Video Call',
                phone: 'Phone Call',
                in_person: 'In-Person Interview'
              };
              const typeLabel = typeLabelMap[interviewType] || 'Video Call';

              const notificationMessage =
                `📅 Interview Scheduled\n\n` +
                `Position: ${interviewData.job_title || selectedConversation.job_title || 'N/A'}\n` +
                `Date: ${dateStr}\n` +
                `Time: ${timeStr}\n` +
                `Duration: ${interviewData.duration || 15} minutes\n` +
                `Type: ${typeLabel}\n` +
                (interviewData.meeting_link ? `Meeting Link: ${interviewData.meeting_link}\n` : '') +
                (interviewData.notes ? `\nNotes: ${interviewData.notes}` : '');

              try {
                const interviewSendResult = await sendMessage(
                  selectedConversation.conversation_id || selectedConversation.id,
                  notificationMessage,
                  [{ type: 'interview' }]
                );
                
                // Broadcast interview notification via shared channel
                if (interviewSendResult?.success && interviewSendResult.data && broadcastChannelRef.current) {
                  const interviewRecipientId = selectedConversation.participant_1 === user.id 
                    ? selectedConversation.participant_2 
                    : selectedConversation.participant_1;
                  const sender = resolveSenderProfile(user.id);
                  broadcastChannelRef.current.send({
                    type: 'broadcast',
                    event: 'new_message',
                    payload: {
                      id: interviewSendResult.data.id,
                      senderId: user.id,
                      recipientId: interviewRecipientId,
                      senderName: sender.name,
                      senderAvatar: sender.avatar,
                      content: interviewSendResult.data.content || notificationMessage,
                      timestamp: interviewSendResult.data.timestamp,
                      type: interviewSendResult.data.type || 'interview_request',
                      isRead: false,
                      readAt: null
                    }
                  });
                }
              } catch (msgErr) {
                // Interview was already persisted; treat message failure as a non-blocking warning
                ErrorHandler.logError('UnifiedMessagingPage', 'Interview saved but failed to send notification message', msgErr);
              }

              // Refresh messages and conversations
              loadMessages(selectedConversation.conversation_id);
              loadConversations();
            } catch (err) {
              ErrorHandler.logError('UnifiedMessagingPage', 'Failed to create interview from messaging', err);
              throw err; // Re-throw so the modal can show the error
            }
          }}
          selectedCandidate={{
            id: selectedConversation.participant_1 === user?.id ? selectedConversation.participant_2 : selectedConversation.participant_1,
            name: selectedConversation.participant_name,
            job_title: selectedConversation.job_title
          }}
          initialData={{
            jobId: selectedConversation.job_id,
            job_title: selectedConversation.job_title,
            conversationId: selectedConversation.conversation_id,
            candidateId: selectedConversation.participant_1 === user?.id ? selectedConversation.participant_2 : selectedConversation.participant_1
          }}
          currentUserId={user?.id}
        />
      )}

      {/* Template Management Modal */}
      <TemplateManagementModal
        isOpen={showTemplateManager}
        onClose={() => {
          setShowTemplateManager(false);
          loadCustomTemplates(); // Refresh templates when modal closes
        }}
        onTemplateSelect={handleTemplateSelectFromManager}
      />

      {/* Quick Actions Modal */}
      <QuickActionsModal
        isOpen={showQuickActionModal}
        onClose={() => {
          setShowQuickActionModal(false);
          setQuickActionType(null);
        }}
        actionType={quickActionType}
        recipientName={selectedConversation?.participant_name}
        isEmployer={isEmployer}
        onSubmit={handleQuickActionSubmit}
      />
    </div>
  );
};

export default UnifiedMessagingPage;

